"use client";

import FallbackImage from "@/app/component/FallbackImage";
import { useClientConfigProvider } from "@/app/config/client-config-provider";
import Constants from "@/app/constants/constants";
import { EventLabels } from "@/app/constants/mix-panel-enums";
import AppRoutes from "@/app/constants/routes";
import { useMixPanel } from "@/app/context/mixPanelContext";
import HotlineV2 from "@/app/versioning/page/LoginPage/v2/loginComponents/hotlineSupportModalV2";
import {
  UniversalStorageConstants,
  UniversalStorageUtils,
} from "@thriwe-2-0/thriwe-ui-utils";
import classNames from "classnames";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function FooterV2() {
  const router = useRouter();
  const pathname = usePathname();
  const { config, getImagePath } = useClientConfigProvider();
  const { t } = useTranslation();
  const { trackEvent } = useMixPanel();

  const [isLogin, setIsLogin] = useState<boolean>(true);
  const [showHotlineModal, setShowHotlineModal] = useState<boolean>(false);

  useEffect(() => {
    const auth = !!UniversalStorageUtils.getItem(
      config?.nativeDataSavingType,
      UniversalStorageConstants.AUTH_TOKEN
    );
    setIsLogin(auth);
  }, []);

  const getFooterClass = () => {
    return Constants.delayBookingRoutes.includes(pathname)
      ? "footer-background-delay-v2"
      : "footer-background-normal-v2";
  };

  return (
    <div className={`footer ${getFooterClass()}`}>
      {/* {Hotline({ showHotlineModal, setShowHotlineModal })} */}

      {showHotlineModal && (
        <HotlineV2
          isOpen={showHotlineModal}
          handleClose={setShowHotlineModal}
        />
      )}

      <div className="footer-container-v2 responsive-inner-padding-v2 max-width-for-larger-devices">
        <div className="d-flex gap-4 flex-column">
          <div className="d-flex justify-content-between flex-md-row flex-column ">
            {!config?.footer.isHideLogo && (
              <div>
                <Image
                  onClick={() => {
                    router.push(AppRoutes.dashboard);
                  }}
                  src={getImagePath("logo.png") ?? ""}
                  alt=""
                  width="150"
                  height="34"
                  className="footer-logo-image"
                />
              </div>
            )}
            {config?.footer?.bottomLinks
              .filter(
                (bottomLink) =>
                  bottomLink.link.filter((link) => {
                    if (link.onLogin) {
                      return link.onLogin == isLogin;
                    } else {
                      return true;
                    }
                  }).length > 0
              )
              .map((footer, index) => {
                return (
                  <div className="footer-item-list" key={index}>
                    <div className="footer-link-header">
                      {t("footer." + footer?.category, footer?.category)}
                    </div>
                    <ul className="footer-link  p-0 list-unstyled">
                      {footer?.link?.map((link, index) => {
                        if (link.onLogin && !isLogin) {
                          return <></>;
                        }
                        return (
                          <li
                            style={{
                              cursor: "pointer",
                              color:
                                pathname == link.routeLink
                                  ? "var(--LightTheme-Primary) !important"
                                  : "",
                            }}
                            onClick={() => {
                              if (link?.routeLink === ``) {
                                setShowHotlineModal(true);
                              } else {
                                router.push(link.routeLink);
                              }
                            }}
                            key={index}
                          >
                            {t("footer." + link?.name, link?.name)}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

            {config?.footer?.showDownloadAppLink && (
              <div className="footer-item-list app-urls">
                <div className="footer-link-header">
                  {t("links.downloadOurApp")}
                </div>
                <ul className="footer-link-social  p-0 list-unstyled">
                  <li style={{ cursor: "pointer" }}>
                    <Image
                      onClick={() => {
                        window.open(config?.footer?.playStoreUrl ?? "");
                        trackEvent(EventLabels.CTA_Play_Store, {
                          screen: "Footer",
                        });
                      }}
                      src={"/common-images/play-store-image.png"}
                      alt=""
                      width="150"
                      height="34"
                      className="app-store-img-style"
                    />
                  </li>
                  <li style={{ cursor: "pointer" }}>
                    <Image
                      onClick={() => {
                        window.open(config?.footer?.appStoreUrl ?? "");
                        trackEvent(EventLabels.CTA_App_Store, {
                          screen: "Footer",
                        });
                      }}
                      src={"/common-images/app-store-image.png"}
                      alt=""
                      width="150"
                      height="34"
                      className="app-store-img-style"
                    />
                  </li>

                  {process.env.NEXT_PUBLIC_PROJECT_CODE === `HDFC_BUNDLE` && (
                    <li style={{ cursor: "pointer" }}>
                      <Image
                        onClick={() => {
                          window.open(`https://mycards.hdfcbank.com/`);
                        }}
                        src={getImagePath("hdfc-mycards.png") ?? ""}
                        alt=""
                        width="150"
                        height="34"
                        className="hdfc-footer-cards-img-style"
                      />
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <hr className="footer-hr" />

      <div
        className={classNames(
          "footer-copyright-box",
          "max-width-for-larger-devices",
          "responsive-inner-padding-v2",
          {
            "padding-bottom-copyright-text": [
              "/golf-detail",
              "/history-detail",
            ]?.includes(pathname),
          }
        )}
      >
        {config?.footer?.isShowPoweredBy && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {t("footer.Powered by")}{" "}
            <FallbackImage
              src={getImagePath("thriwe-logo.png")}
              fallbackSrc={""}
              height={24}
              width={82}
              alt={"Logo"}
            />
          </div>
        )}
        <div
          className={`copyright-text ${
            config?.footer?.socialMedia?.length === 0 &&
            !config?.footer?.isShowPoweredBy &&
            "txt_center"
          }`}
        >
          {t("footer.copyRightTextInFooter")}
        </div>

        {(config?.footer?.socialMedias ?? []).length > 0 && (
          <div className="footer-social-media-style ">
            {config?.footer?.socialMedias?.map(
              (
                { ReactIcon, redirectUrl, defaultColor, hoveredColor, size },
                idx
              ) => (
                <a
                  key={idx}
                  href={redirectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-media-icons"
                  style={{
                    color: defaultColor,
                    transition: "color 0.5s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = hoveredColor)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = defaultColor)
                  }
                >
                  <ReactIcon className="social-icon" size={size} />
                </a>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
